import { createSlice } from "@reduxjs/toolkit";
import { authAPI } from "../../services/auth/auth.service";
import { AccountType, User } from "../../services/auth/dto/auth.dto";
import { RootState } from "../../index";
import { Placement } from "react-joyride";
import { GUIDE_STEPS } from "./guide_steps";
interface authState {
	user: User | null;
	isLoading: boolean;
	isLoggedIn: boolean;
	runGuide: boolean;
	guideSteps: {
		target: string;
		content: string;
		disableBeacon?: boolean;
		placement?: Placement | "center";
		continuous?: boolean;
		locale?: {
			close: string;
		};
	}[];
}

const initialState: authState = {
	user: null,
	isLoading: false,
	isLoggedIn: false,
	runGuide: false,
	guideSteps: GUIDE_STEPS
};

const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		logOut: state => {
			state.user = null;
			state.isLoggedIn = false;
		},
		setLoadingState: (state, action) => {
			state.isLoading = action.payload;
		},
		setRunGuide: (state, action) => {
			state.runGuide = action.payload;
		}
	},
	extraReducers: builder =>
		builder
			.addMatcher(authAPI.endpoints.signUp.matchPending, state => {
				state.isLoading = true;
			})
			.addMatcher(authAPI.endpoints.signUp.matchFulfilled, (state, action) => {
				const { user, token } = action.payload as { user: User; token: string };
				state.user = user;
				state.isLoggedIn = true;
				localStorage.setItem("token", token);
				state.isLoading = false;
			})
			.addMatcher(authAPI.endpoints.signUp.matchRejected, state => {
				state.isLoading = false;
				state.isLoggedIn = false;
			})
			.addMatcher(authAPI.endpoints.login.matchPending, state => {
				state.isLoading = true;
			})
			.addMatcher(authAPI.endpoints.login.matchFulfilled, (state, action) => {
				const { user, token } = action.payload as { user: User; token: string };
				state.user = user;
				state.isLoggedIn = true;
				localStorage.setItem("token", token);
				state.isLoading = false;
			})
			.addMatcher(authAPI.endpoints.login.matchRejected, state => {
				state.isLoading = false;
				state.isLoggedIn = false;
			})
			.addMatcher(authAPI.endpoints.fetchUserData.matchPending, state => {
				state.isLoading = true;
			})
			.addMatcher(authAPI.endpoints.fetchUserData.matchFulfilled, (state, action) => {
				const { user, token } = action.payload as { user: User; token: string };
				if (!user) {
					return localStorage.clear();
				}
				state.user = user;
				state.isLoggedIn = true;
				localStorage.setItem("token", token);
				state.isLoading = false;
			})
			.addMatcher(authAPI.endpoints.fetchUserData.matchRejected, state => {
				state.isLoading = false;
				state.isLoggedIn = false;
			})
			.addMatcher(authAPI.endpoints.verifyEmail.matchFulfilled, state => {
				if (state.user) state.user.email_verified = true;
			})
			.addMatcher(authAPI.endpoints.loginWithGoogle.matchFulfilled, (state, action) => {
				const { user, token } = action.payload as { user: User; token: string };
				state.user = user;
				state.isLoggedIn = true;
				localStorage.setItem("token", token);
				state.isLoading = false;
			})
});
export const { logOut, setRunGuide, setLoadingState } = authSlice.actions;
export const getFullState = (state: RootState): authState => state.auth;
export const getIsLoggedIn = (state: RootState): boolean => state.auth.isLoggedIn;
export const getIsSuperAdmin = (state: RootState): boolean =>
	state.auth.user ? state.auth.user.account_type === AccountType.ADMIN : false;
export default authSlice.reducer;
